import React, { useState } from "react";
import { Box, Button, Container, Typography, TextField, Paper } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { createHint } from "../services/api";
import { useAuth } from "../contexts/AuthContext";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import ChatHintList from './ChatHintList';

const HintRegister: React.FC = () => {
    const [hint, setHint] = useState<string>("");
    const [image, setImage] = useState<File | null>(null);
    const [previewUrl, setPreviewUrl] = useState<string | null>(null);
    const [location, setLocation] = useState<{ latitude: number; longitude: number } | null>(null);
    const [previewOpen, setPreviewOpen] = useState(false);
    const navigate = useNavigate();
    const { token, refreshTokenFunction, logout } = useAuth();

    const handleHintSubmit = async () => {
        if (!hint.trim() && !image && !location) {
            alert("ヒント、画像、または現在地情報を入力してください。");
            return;
        }

        try {
            if (token) {
                await createHint(
                    token,
                    hint,
                    image,
                    location?.latitude || null,
                    location?.longitude || null
                );
                alert("ヒントが投稿されました！");
                navigate("/staff-dashboard");
            }
        } catch (error: any) {
            // 401の場合はリフレッシュトークンで再取得を試みる
            if (error.response && error.response.status === 401) {
                try {
                    const newToken = await refreshTokenFunction();
                    if (newToken) {
                        await createHint(
                            newToken,
                            hint,
                            image,
                            location?.latitude || null,
                            location?.longitude || null
                        );
                        alert("ヒントが投稿されました！");
                        navigate("/staff-dashboard");
                        return;
                    }
                } catch (refreshError) {}
            }
            console.error("ヒントの投稿に失敗しました:", error);
            alert("ヒントの投稿に失敗しました。");
            logout();
        }
    };

    const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && e.target.files[0]) {
            const file = e.target.files[0];
            setImage(file);
            setPreviewUrl(URL.createObjectURL(file));
        }
    };

    // 投稿ボタン押下時: プレビューダイアログを開く
    const handlePreviewOpen = () => {
        if (!hint.trim() && !image) {
            alert("ヒントまたは画像を入力してください。");
            return;
        }
        setPreviewOpen(true);
    };

    // プレビューでOK時: 実際の投稿処理
    const handlePreviewSubmit = async () => {
        setPreviewOpen(false);
        await handleHintSubmit();
    };

    // プレビューダイアログ用サンプルデータ
    const previewHintId = 9999;
    const previewHints = [
        {
            id: previewHintId,
            text: hint,
            image: previewUrl || undefined,
            comments: [],
        },
        {
            id: 2,
            text: "ヒント：サンプル",
            image: undefined,
            comments: [],
        },
        {
            id: 1,
            text: "ヒント：サンプル",
            image: undefined,
            comments: [],
        },
    ];

    const previewComments = {
        [previewHintId]: [
            { id: 1, text: "スタッフによるサンプルコメント", created_by_name: "スタッフ", created_at: new Date().toISOString(), hint: previewHintId }
        ],
        2: [
            { id: 2, text: "一般ユーザーのコメント", created_by_name: "ユーザーA", created_at: new Date().toISOString(), hint: 2 }
        ],
        1: [],
    };

    const previewCommentInputs = { [previewHintId]: "", 2: "", 1: "" };
    const previewShowComments = { [previewHintId]: false, 2: false, 1: false };

    return (
        <Container maxWidth="sm" sx={{ mt: '72px', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <Box width="100%" display="flex" flexDirection="column" alignItems="center">
                <Typography variant="h4" align="center" gutterBottom sx={{ fontWeight: 800, color: '#1976d2', letterSpacing: 2, mb: 2, fontFamily: 'Poppins' }}>
                    ヒント投稿
                </Typography>
                <Paper sx={{ mt: 2, p: { xs: 2, sm: 4 }, borderRadius: 4, boxShadow: '0 8px 32px rgba(33,150,243,0.10)', background: 'linear-gradient(135deg, #e3f2fd 0%, #fff 100%)', width: '100%', maxWidth: 480 }}>
                    <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: '#1976d2', mb: 2 }}>
                        新しいヒントを入力してください
                    </Typography>
                    <TextField
                        label="ヒントを入力"
                        value={hint}
                        onChange={(e) => setHint(e.target.value)}
                        fullWidth
                        multiline
                        minRows={2}
                        sx={{ mb: 3, background: '#fff', borderRadius: 2, boxShadow: '0 1px 4px #e3f2fd' }}
                    />
                    <Button
                        variant="outlined"
                        component="label"
                        sx={{ mb: 2, borderRadius: 3, borderColor: '#1976d2', color: '#1976d2', fontWeight: 700, px: 3, py: 1, boxShadow: '0 2px 8px #bbdefb', '&:hover': { background: '#e3f2fd', borderColor: '#1976d2' } }}
                        fullWidth
                    >
                        画像を添付
                        <input
                            type="file"
                            accept="image/*"
                            hidden
                            onChange={handleImageChange}
                        />
                    </Button>
                    {previewUrl && (
                        <Box sx={{ mb: 3, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <Typography variant="body2" gutterBottom sx={{ color: '#1976d2', fontWeight: 500 }}>
                                プレビュー:
                            </Typography>
                            <Box sx={{ borderRadius: 2, overflow: 'hidden', boxShadow: '0 2px 8px #bbdefb', border: '1.5px solid #90caf9', p: 1, background: '#fff' }}>
                                <img
                                    src={previewUrl}
                                    alt="プレビュー画像"
                                    style={{ maxWidth: 240, maxHeight: 180, borderRadius: 8 }}
                                />
                            </Box>
                        </Box>
                    )}
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handlePreviewOpen}
                        fullWidth
                        sx={{ mt: 1, fontWeight: 800, borderRadius: 3, fontSize: '1.1rem', py: 1.2, boxShadow: '0 4px 16px #90caf9', letterSpacing: 1 }}
                    >
                        投稿
                    </Button>
                </Paper>
                <Box display="flex" justifyContent="center" mt={4} width="100%">
                    <Button variant="outlined" onClick={() => navigate("/staff-dashboard")}
                        sx={{ borderRadius: 3, borderColor: '#1976d2', color: '#1976d2', fontWeight: 700, px: 4, py: 1, boxShadow: '0 2px 8px #bbdefb', '&:hover': { background: '#e3f2fd', borderColor: '#1976d2' } }}
                    >
                        戻る
                    </Button>
                </Box>
            </Box>
            {/* 投稿前プレビューダイアログ */}
            <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="xs" fullWidth>
                <DialogTitle sx={{ fontWeight: 700, color: '#d81b60', textAlign: 'center' }}>投稿内容の確認</DialogTitle>
                <DialogContent>
                    {/* チャット風プレビュー */}
                    <ChatHintList
                        hints={previewHints}
                        comments={previewComments}
                        commentInputs={previewCommentInputs}
                        showComments={previewShowComments}
                        handleCommentSend={() => {}} // プレビューなので空実装
                        setShowComments={() => {}}
                        setCommentInputs={() => {}}
                    />
                    <Typography variant="body2" color="text.secondary" align="center" sx={{ mt: 2 }}>
                        この内容でイベントルームに投稿します。よろしいですか？
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
                    <Button onClick={() => setPreviewOpen(false)} variant="outlined" color="secondary">キャンセル</Button>
                    <Button onClick={handlePreviewSubmit} variant="contained" color="primary">投稿する</Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default HintRegister;